import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Search, Plus, Pencil, Trash2, ChevronRight, X, Package, Archive, RotateCcw, Check, ChevronsUpDown, Download, Copy, Sticker } from 'lucide-react';
import { StickerToggle, StickerPicker } from '../components/Stickers';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { normalizeCategory } from '../lib/categories';
import { formatQuantity, formatCost } from '../utils/formatters';
import PackingGroupsEditor from './PackingGroupsEditor';

const FinalRecipesPage = () => {
  const { isAdmin } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [subRecipes, setSubRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [activeSize, setActiveSize] = useState('');
  const [openCompSearch, setOpenCompSearch] = useState(null); // "sizeIdx-compIdx"

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    type: 'Standard',
    requires_packing_sheet: false,
    sticker_required: false,
    sticker_ids: [],
    notes: '',
    sop: '',
    size_variations: [{ size_name: 'Standard', components: [], total_cost: 0 }]
  });

  const fetchRecipes = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (categoryFilter && categoryFilter !== 'all') params.category = categoryFilter;
      if (showArchived) params.include_archived = true;
      
      const response = await axios.get(`${API}/recipes`, { params });
      setRecipes(response.data);
    } catch (error) {
      toast.error('Failed to fetch recipes');
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, showArchived]);

  const fetchDependencies = async () => {
    try {
      const [ingredientsRes, subRecipesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/ingredients`),
        axios.get(`${API}/sub-recipes`),
        axios.get(`${API}/categories`)
      ]);
      setIngredients(ingredientsRes.data);
      setSubRecipes(subRecipesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Failed to fetch dependencies');
    }
  };

  useEffect(() => {
    fetchRecipes();
    fetchDependencies();
  }, [fetchRecipes]);

  useEffect(() => {
    if (selectedRecipe?.size_variations?.length > 0) {
      setActiveSize(selectedRecipe.size_variations[0].size_name);
    }
  }, [selectedRecipe]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.sticker_required && !(formData.sticker_ids || []).length) {
      toast.error("Choose at least one sticker, or turn off 'Sticker Required'");
      return;
    }
    try {
      const payload = {
        ...formData,
        size_variations: formData.size_variations.map(sv => ({
          ...sv,
          components: sv.components.map(c => ({
            ...c,
            weight: parseFloat(c.weight)
          }))
        }))
      };

      if (editingRecipe) {
        await axios.put(`${API}/recipes/${editingRecipe.id}`, payload);
        toast.success('Recipe updated');
        // Refresh detail view
        const res = await axios.get(`${API}/recipes/${editingRecipe.id}`);
        setSelectedRecipe(res.data);
      } else {
        await axios.post(`${API}/recipes`, payload);
        toast.success('Recipe created');
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchRecipes();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save recipe');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recipe?')) return;
    try {
      await axios.delete(`${API}/recipes/${id}`);
      toast.success('Recipe deleted');
      if (selectedRecipe?.id === id) setSelectedRecipe(null);
      fetchRecipes();
    } catch (error) {
      toast.error('Failed to delete recipe');
    }
  };

  const handleArchiveToggle = async (id, currentlyArchived) => {
    try {
      await axios.put(`${API}/recipes/${id}/archive`, { is_archived: !currentlyArchived });
      toast.success(currentlyArchived ? 'Recipe restored' : 'Recipe archived');
      if (selectedRecipe?.id === id) setSelectedRecipe(null);
      fetchRecipes();
    } catch (error) {
      toast.error('Failed to update recipe');
    }
  };

  const handleCopy = async (recipe) => {
    const suggested = `${recipe.name} (Copy)`;
    // eslint-disable-next-line no-alert
    const newName = window.prompt(
      `Enter a unique name for the copy of "${recipe.name}":`,
      suggested
    );
    if (newName === null) return; // user cancelled
    const trimmed = newName.trim();
    if (!trimmed) {
      toast.error('Name cannot be empty');
      return;
    }
    try {
      const res = await axios.post(`${API}/recipes/${recipe.id}/copy`, { new_name: trimmed });
      toast.success(`Recipe copied as "${trimmed}"`);
      await fetchRecipes();
      // Auto-select the new copy so the user can immediately edit it
      if (res.data?.id) {
        try {
          const full = await axios.get(`${API}/recipes/${res.data.id}`);
          setSelectedRecipe(full.data);
        } catch { /* ignore, list already refreshed */ }
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409) {
        toast.error(detail || 'A recipe with that name already exists');
      } else {
        toast.error(detail || 'Failed to copy recipe');
      }
    }
  };

  const openEditDialog = (recipe) => {
    setEditingRecipe(recipe);
    // Deep clone to prevent mutating the original recipe object
    setFormData({
      name: recipe.name,
      category: recipe.category,
      type: recipe.type,
      requires_packing_sheet: recipe.requires_packing_sheet,
      sticker_required: !!recipe.sticker_required,
      sticker_ids: (recipe.sticker_ids || []).filter(id => stickerTypes.some(s => s.id === id)),
      notes: recipe.notes || '',
      sop: recipe.sop || '',
      size_variations: JSON.parse(JSON.stringify(recipe.size_variations))
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingRecipe(null);
    setFormData({
      name: '',
      category: '',
      type: 'Standard',
      requires_packing_sheet: false,
      sticker_required: false,
      sticker_ids: [],
      notes: '',
      sop: '',
      size_variations: [{ size_name: 'Standard', components: [], total_cost: 0 }]
    });
  };

  // Size variation management
  const addSizeVariation = () => {
    setFormData(prev => ({
      ...prev,
      size_variations: [...prev.size_variations, { size_name: '', components: [], total_cost: 0 }]
    }));
  };

  const updateSizeVariation = (index, field, value) => {
    setFormData(prev => {
      const updated = JSON.parse(JSON.stringify(prev.size_variations));
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, size_variations: updated };
    });
  };

  const removeSizeVariation = (index) => {
    setFormData(prev => ({
      ...prev, size_variations: prev.size_variations.filter((_, i) => i !== index)
    }));
  };

  const addComponent = (sizeIndex) => {
    setFormData(prev => {
      const updated = JSON.parse(JSON.stringify(prev.size_variations));
      updated[sizeIndex].components.push({
        component_type: 'ingredient', component_id: '', component_name: '',
        weight: '', weight_type: 'g', packaging: '', packaging_notes: ''
      });
      return { ...prev, size_variations: updated };
    });
  };

  const updateComponent = (sizeIndex, compIndex, field, value) => {
    setFormData(prev => {
      const updated = JSON.parse(JSON.stringify(prev.size_variations));
      const comp = updated[sizeIndex].components[compIndex];
      if (field === 'component_id') {
        const type = comp.component_type;
        const source = type === 'sub_recipe' ? subRecipes : ingredients;
        const item = source.find(i => i.id === value);
        comp.component_id = value;
        comp.component_name = item?.name || '';
      } else if (field === 'component_type') {
        comp.component_type = value;
        comp.component_id = '';
        comp.component_name = '';
      } else {
        comp[field] = value;
      }
      return { ...prev, size_variations: updated };
    });
  };

  const removeComponent = (sizeIndex, compIndex) => {
    setFormData(prev => {
      const updated = JSON.parse(JSON.stringify(prev.size_variations));
      updated[sizeIndex].components = updated[sizeIndex].components.filter((_, i) => i !== compIndex);
      return { ...prev, size_variations: updated };
    });
  };

  const exportRecipesExcel = async () => {
    try {
      const response = await axios.get(`${API}/final-recipes/export-excel`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Final_Recipes_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Recipes exported');
    } catch (error) {
      toast.error('Failed to export recipes');
    }
  };

  const recipeCategories = categories.filter(c => c.type === 'recipe_category');
  const mealTypes = categories.filter(c => c.type === 'meal_type');
  const stickerTypes = categories.filter(c => c.type === 'sticker_type').sort((a, b) => a.name.localeCompare(b.name));
  const stickerNames = (r) => (r?.sticker_required ? (r.sticker_ids || []).map(id => stickerTypes.find(s => s.id === id)?.name).filter(Boolean) : []);
  const sortedIngredients = [...ingredients].sort((a, b) => a.name.localeCompare(b.name));
  const sortedSubRecipes = [...subRecipes].sort((a, b) => a.name.localeCompare(b.name));

  const renderSizeVariations = (recipe) => (
    <Tabs value={activeSize} onValueChange={setActiveSize}>
      <TabsList className="bg-zinc-100 p-1 rounded-none">
        {recipe.size_variations.map((sv) => (
          <TabsTrigger key={sv.size_name} value={sv.size_name}
            className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none px-6"
            data-testid={`size-tab-${sv.size_name}`}>
            {sv.size_name}
            {isAdmin && <span className="ml-2 font-mono text-xs tabular-nums">{formatCost(sv.total_cost)}</span>}
          </TabsTrigger>
        ))}
      </TabsList>
      {recipe.size_variations.map((sv) => (
        <TabsContent key={sv.size_name} value={sv.size_name} className="mt-4">
          <table className="data-table">
            <thead><tr><th>Component</th><th>Type</th><th>Weight</th><th>Packaging</th>{isAdmin && <th>Cost</th>}</tr></thead>
            <tbody>
              {sv.components.map((comp, idx) => {
                let unitCost = 0;
                if (comp.component_type === 'ingredient') {
                  const ing = ingredients.find(i => i.id === comp.component_id);
                  unitCost = ing ? ing.cost_per_unit * comp.weight : 0;
                } else {
                  const sub = subRecipes.find(s => s.id === comp.component_id);
                  unitCost = sub ? sub.cost_per_unit * comp.weight : 0;
                }
                return (
                  <tr key={comp.component_name}>
                    <td className="font-medium">{comp.component_name}</td>
                    <td><span className={`px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-bold ${comp.component_type === 'sub_recipe' ? 'bg-blue-50 text-blue-700' : 'bg-zinc-100 text-zinc-600'}`}>{comp.component_type === 'sub_recipe' ? 'Sub-Recipe' : 'Ingredient'}</span></td>
                    <td className="tabular-nums" style={{ fontFamily: 'var(--font-mono)' }}>{formatQuantity(comp.weight)} {comp.weight_type}</td>
                    <td><div className="text-sm">{comp.packaging || '-'}</div>{comp.packaging_notes && <div className="text-xs text-zinc-500">{comp.packaging_notes}</div>}</td>
                    {isAdmin && <td className="tabular-nums" style={{ fontFamily: 'var(--font-mono)' }}>{formatCost(unitCost)}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {isAdmin && (
            <div className="mt-4 pt-4 border-t border-zinc-200 flex justify-end">
              <div className="text-right">
                <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-500 mr-4">Total Recipe Cost</span>
                <span className="font-mono tabular-nums text-lg font-medium">{formatCost(sv.total_cost)}</span>
              </div>
            </div>
          )}
        </TabsContent>
      ))}
    </Tabs>
  );

  return (
    <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8" data-testid="final-recipes-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl sm:text-3xl tracking-tight font-medium text-zinc-950" style={{ fontFamily: 'var(--font-heading)' }}>
          Final Recipes
        </h1>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button
              onClick={exportRecipesExcel}
              variant="outline"
              className="px-4 py-2 font-medium border-zinc-300"
              data-testid="export-recipes-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export (Excel)
            </Button>
            <Button
              onClick={() => { resetForm(); setIsDialogOpen(true); }}
              className="btn-primary px-6 py-2 font-medium"
              data-testid="add-recipe-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Recipe
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search recipes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="recipe-search-input"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-[200px]" data-testid="category-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {recipeCategories.map((cat) => (
              <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isAdmin && (
          <button
            onClick={() => setShowArchived(!showArchived)}
            className={`flex items-center gap-2 px-4 py-2 text-sm border transition-colors ${
              showArchived ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
            }`}
            style={{ borderRadius: '9999px' }}
            data-testid="show-archived-toggle"
          >
            <Archive className="w-4 h-4" />
            {showArchived ? 'Showing Archived' : 'Show Archived'}
          </button>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="progress-bar mb-4">
          <div className="progress-bar-fill"></div>
        </div>
      )}

      {/* Split View */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
        {/* Recipe List */}
        <div className="bg-white lg:col-span-1 max-h-[700px] overflow-y-auto">
          <div className="sticky top-0 bg-zinc-100 px-4 py-3 border-b border-zinc-200">
            <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-500">
              Recipes ({recipes.length})
            </span>
          </div>
          {recipes.length === 0 ? (
            <div className="p-6 text-center text-zinc-500">
              No recipes found
            </div>
          ) : (
            recipes.map((recipe) => (
              <div
                key={recipe.id}
                onClick={() => setSelectedRecipe(recipe)}
                className={`px-4 py-3 border-b border-zinc-200 cursor-pointer transition-colors ${
                  selectedRecipe?.id === recipe.id ? 'bg-zinc-100' : 'hover:bg-zinc-50'
                }`}
                data-testid={`recipe-item-${recipe.id}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium text-sm">
                      {recipe.name}
                      {recipe.is_archived && <span className="ml-2 badge-pill bg-amber-100 text-amber-700 text-[9px]">archived</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="badge-pill badge-gray text-[10px]">{normalizeCategory(recipe.category)}</span>
                      <span className="badge-pill badge-gray text-[10px]">{recipe.type}</span>
                      {recipe.requires_packing_sheet && (
                        <Package className="w-3 h-3 text-gray-400" title="Requires packing sheet" />
                      )}
                      {stickerNames(recipe).length > 0 && (
                        <span title={`Stickers: ${stickerNames(recipe).join(', ')}`} data-testid={`recipe-sticker-icon-${recipe.id}`}>
                          <Sticker className="w-3 h-3 text-purple-600" />
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 mt-1" />
                </div>
              </div>
            ))
          )}
        </div>

        {/* Recipe Detail */}
        <div className="bg-white lg:col-span-2 min-h-[500px]">
          {selectedRecipe ? (
            <div>
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
                <div>
                  <h2 className="text-xl font-medium" style={{ fontFamily: 'var(--font-heading)' }}>
                    {selectedRecipe.name}
                  </h2>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="px-2 py-1 bg-zinc-100 text-[10px] uppercase tracking-wider font-bold text-zinc-600">
                      {normalizeCategory(selectedRecipe.category)}
                    </span>
                    <span className="px-2 py-1 bg-zinc-100 text-[10px] uppercase tracking-wider font-bold text-zinc-600">
                      {selectedRecipe.type}
                    </span>
                    {selectedRecipe.requires_packing_sheet && (
                      <span className="flex items-center gap-1 px-2 py-1 bg-emerald-50 text-[10px] uppercase tracking-wider font-bold text-emerald-700 border border-emerald-200">
                        <Package className="w-3 h-3" />
                        Packing Sheet
                      </span>
                    )}
                    {stickerNames(selectedRecipe).length > 0 && (
                      <span className="flex items-center gap-1 px-2 py-1 bg-purple-50 text-[10px] uppercase tracking-wider font-bold text-purple-700 border border-purple-200" data-testid="recipe-detail-stickers">
                        <Sticker className="w-3 h-3" />
                        {stickerNames(selectedRecipe).join(' · ')}
                      </span>
                    )}
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEditDialog(selectedRecipe)}
                      className="p-2 border border-gray-200 hover:bg-gray-100 rounded-lg transition-colors"
                      data-testid="edit-recipe-btn"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleCopy(selectedRecipe)}
                      className="p-2 border border-gray-200 hover:bg-blue-50 hover:border-blue-200 text-blue-600 rounded-lg transition-colors"
                      data-testid="copy-recipe-btn"
                      title="Duplicate this recipe"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleArchiveToggle(selectedRecipe.id, selectedRecipe.is_archived)}
                      className={`p-2 border rounded-lg transition-colors ${
                        selectedRecipe.is_archived 
                          ? 'border-green-200 hover:bg-green-50 text-green-600' 
                          : 'border-amber-200 hover:bg-amber-50 text-amber-600'
                      }`}
                      data-testid="archive-recipe-btn"
                      title={selectedRecipe.is_archived ? 'Restore recipe' : 'Archive recipe'}
                    >
                      {selectedRecipe.is_archived ? <RotateCcw className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleDelete(selectedRecipe.id)}
                      className="p-2 border border-gray-200 hover:bg-red-50 hover:border-red-200 text-red-600 rounded-lg transition-colors"
                      data-testid="delete-recipe-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Notes */}
              {selectedRecipe.notes && (
                <div className="px-6 py-3 bg-zinc-50 border-b border-zinc-200 text-sm text-zinc-600">
                  {selectedRecipe.notes}
                </div>
              )}

              {/* SOP */}
              {selectedRecipe.sop && selectedRecipe.sop.trim() && (
                <div className="px-6 py-3 border-b border-zinc-200 bg-amber-50" data-testid="final-recipe-sop-display">
                  <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-amber-700 mb-1">SOP</div>
                  <div className="text-sm whitespace-pre-wrap text-gray-800">{selectedRecipe.sop}</div>
                </div>
              )}

              {/* Recipe Tabs: Sizes + Packing */}
              <div className="px-6 py-4">
                {selectedRecipe.requires_packing_sheet ? (
                  <Tabs defaultValue="sizes">
                    <TabsList className="bg-zinc-100 p-1 rounded-none mb-4">
                      <TabsTrigger value="sizes" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none px-6">
                        Sizes & Components
                      </TabsTrigger>
                      <TabsTrigger value="packing" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none px-6" data-testid="recipe-packing-tab">
                        <Package className="w-3.5 h-3.5 mr-1.5" />
                        Packing
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="sizes">
                      {renderSizeVariations(selectedRecipe)}
                    </TabsContent>

                    <TabsContent value="packing">
                      <PackingGroupsEditor
                        recipe={selectedRecipe}
                        isAdmin={isAdmin}
                        onUpdate={(updated) => { setSelectedRecipe(updated); fetchRecipes(); }}
                      />
                    </TabsContent>
                  </Tabs>
                ) : (
                  renderSizeVariations(selectedRecipe)
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-zinc-500">
              <div className="text-center">
                <div 
                  className="w-48 h-48 mx-auto mb-4 bg-cover bg-center opacity-30"
                  style={{ backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/1fd1791e-2ec7-41c6-806e-b3dd968bd74e/images/236a3b9d4896cab66e6d7a806415bbba8affe75126deb23767efeb8554ae0812.png)` }}
                />
                <p>Select a recipe to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
              {editingRecipe ? 'Edit Recipe' : 'Add New Recipe'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Recipe Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Chicken Katsu Rice Bowl"
                    className="rounded-none border-zinc-300"
                    required
                    data-testid="recipe-name-input"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="category">Category</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                    <SelectTrigger className="rounded-none border-zinc-300" data-testid="recipe-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {recipeCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="type">Meal Type</Label>
                  <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                    <SelectTrigger className="rounded-none border-zinc-300" data-testid="recipe-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {mealTypes.map((type) => (
                        <SelectItem key={type.id} value={type.name}>{type.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Requires Packing Sheet</Label>
                  <div className="flex items-center gap-2 pt-2">
                    <Switch
                      checked={formData.requires_packing_sheet}
                      onCheckedChange={(checked) => setFormData({ ...formData, requires_packing_sheet: checked })}
                      data-testid="packing-sheet-toggle"
                    />
                    <span className="text-sm text-zinc-600">
                      {formData.requires_packing_sheet ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
                <StickerToggle formData={formData} setFormData={setFormData} />
              </div>

              <StickerPicker stickers={stickerTypes} formData={formData} setFormData={setFormData} />

              <div className="grid gap-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Optional notes..."
                  className="rounded-none border-zinc-300 min-h-[60px]"
                  data-testid="recipe-notes-input"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="sop">SOP (Standard Operating Procedure)</Label>
                <Textarea
                  id="sop"
                  value={formData.sop}
                  onChange={(e) => setFormData({ ...formData, sop: e.target.value })}
                  placeholder="Prep and cooking notes for kitchen reference..."
                  className="rounded-none border-zinc-300 min-h-[90px]"
                  data-testid="recipe-sop-input"
                />
              </div>

              {/* Size Variations */}
              <div className="border border-zinc-200 p-4">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-500">
                    Size Variations
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addSizeVariation}
                    className="rounded-none border-zinc-300 text-xs"
                    data-testid="add-size-variation-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Size
                  </Button>
                </div>

                {formData.size_variations.map((sv, sizeIdx) => (
                  <div key={sizeIdx} className="border border-zinc-200 p-4 mb-4 last:mb-0">
                    <div className="flex items-center justify-between mb-3">
                      <Input
                        value={sv.size_name}
                        onChange={(e) => updateSizeVariation(sizeIdx, 'size_name', e.target.value)}
                        placeholder="Size name (e.g., Small, Large)"
                        className="rounded-none border-zinc-300 max-w-[200px]"
                        required
                      />
                      {formData.size_variations.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSizeVariation(sizeIdx)}
                          className="p-1 text-red-500 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Components */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-zinc-500">Components</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addComponent(sizeIdx)}
                          className="rounded-none border-zinc-300 text-xs h-7"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Component
                        </Button>
                      </div>

                      {sv.components.length === 0 ? (
                        <div className="text-xs text-zinc-400 py-2 text-center">
                          No components added
                        </div>
                      ) : (
                        sv.components.map((comp, compIdx) => (
                          <div key={compIdx} className="p-3 bg-gray-50 border border-gray-200 rounded-lg mb-2">
                            <div className="flex gap-2 mb-2">
                              <Select value={comp.component_type}
                                onValueChange={(value) => updateComponent(sizeIdx, compIdx, 'component_type', value)}>
                                <SelectTrigger className="w-[130px] text-xs h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="ingredient">Ingredient</SelectItem>
                                  <SelectItem value="sub_recipe">Sub-Recipe</SelectItem>
                                </SelectContent>
                              </Select>
                              <Popover open={openCompSearch === `${sizeIdx}-${compIdx}`}
                                onOpenChange={(open) => setOpenCompSearch(open ? `${sizeIdx}-${compIdx}` : null)}>
                                <PopoverTrigger asChild>
                                  <Button variant="outline" className="flex-1 justify-between text-sm font-normal h-9">
                                    <span className="truncate">{comp.component_name || 'Search...'}</span>
                                    <ChevronsUpDown className="w-3 h-3 ml-1 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[380px] p-0" style={{ borderRadius: '12px' }}>
                                  <Command>
                                    <CommandInput placeholder={`Search ${comp.component_type === 'sub_recipe' ? 'sub-recipes' : 'ingredients'}...`} />
                                    <CommandList>
                                      <CommandEmpty>Not found.</CommandEmpty>
                                      <CommandGroup>
                                        {(comp.component_type === 'sub_recipe' ? sortedSubRecipes : sortedIngredients).map(item => (
                                          <CommandItem key={item.id} value={item.name} onSelect={() => {
                                            updateComponent(sizeIdx, compIdx, 'component_id', item.id);
                                            setOpenCompSearch(null);
                                          }}>
                                            <Check className={`w-4 h-4 mr-2 shrink-0 ${comp.component_id === item.id ? 'opacity-100' : 'opacity-0'}`} />
                                            <span className="truncate">{item.name}</span>
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                              <button type="button" onClick={() => removeComponent(sizeIdx, compIdx)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded shrink-0">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="flex gap-2">
                              <Input type="number" step="0.01" value={comp.weight}
                                onChange={(e) => updateComponent(sizeIdx, compIdx, 'weight', e.target.value)}
                                placeholder="Weight" className="w-[100px] text-xs h-8" />
                              <Select value={comp.weight_type}
                                onValueChange={(value) => updateComponent(sizeIdx, compIdx, 'weight_type', value)}>
                                <SelectTrigger className="w-[70px] text-xs h-8"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="g">g</SelectItem>
                                  <SelectItem value="pieces">pcs</SelectItem>
                                </SelectContent>
                              </Select>
                              <Input value={comp.packaging || ''}
                                onChange={(e) => updateComponent(sizeIdx, compIdx, 'packaging', e.target.value)}
                                placeholder="Packaging (e.g. Tray + Film)" className="flex-1 text-xs h-8" />
                              <Input value={comp.packaging_notes || ''}
                                onChange={(e) => updateComponent(sizeIdx, compIdx, 'packaging_notes', e.target.value)}
                                placeholder="Notes (e.g. MAX 9)" className="w-[120px] text-xs h-8" />
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="rounded-none border-zinc-300"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="rounded-none bg-zinc-950 text-white hover:bg-zinc-800"
                data-testid="save-recipe-btn"
              >
                {editingRecipe ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinalRecipesPage;
